import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, API_BASE, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { CurrencyInr, DownloadSimple, CheckSquare } from "@phosphor-icons/react";

export default function PartnerPayouts() {
  const [summary, setSummary] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, g] = await Promise.all([
        api.get("/gig-partners/payouts/summary"),
        api.get("/gig-partners/payouts"),
      ]);
      setSummary(s.data); setGroups(g.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (eid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(eid)) next.delete(eid); else next.add(eid);
      return next;
    });
  };

  const markPaid = async () => {
    if (selected.size === 0) return;
    if (!reference.trim()) {
      toast.error("Enter a reference (UTR / cheque / UPI)");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/gig-partners/payouts/mark-paid", {
        earning_ids: Array.from(selected),
        reference: reference.trim(),
        note: note.trim() || undefined,
      });
      toast.success(`Marked ${r.data.updated} earning(s) paid`);
      setSelected(new Set()); setReference(""); setNote("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("rescigo_token");
      const res = await fetch(`${API_BASE}/gig-partners/payouts/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "gig-payouts.csv";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const Card = ({ label, value, accent }) => (
    <div className="p-4 rounded-lg bg-white border border-[#E5E2D9]">
      <div className="text-[11px] uppercase tracking-wider text-[#8A8780]">{label}</div>
      <div className={`text-2xl font-heading mt-1 ${accent || ""}`}>₹ {value?.toFixed?.(2) ?? "0.00"}</div>
    </div>
  );

  return (
    <AppShell title="Partner Payouts">
      <div data-testid="partner-payouts" className="space-y-5">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8A8780]">Finance</div>
            <h1 className="text-2xl font-heading mt-1 flex items-center gap-2">
              <CurrencyInr size={22} weight="duotone"/>
              Partner Payouts
            </h1>
            <p className="text-sm text-[#5C5A55] mt-1">
              Settle gig-partner earnings. Pay outside the system then mark the rows as paid with a reference note.
            </p>
          </div>
          <button onClick={exportCsv} data-testid="payouts-export"
                   className="h-9 px-3 rounded-md border border-[#D9D5C6] bg-white text-xs flex items-center gap-2">
            <DownloadSimple size={14}/> Export CSV
          </button>
        </div>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card label="Today" value={summary.total_today}/>
            <Card label="Payable now" value={summary.payable_now} accent="text-[#2D5A27]"/>
            <Card label="Paid · last 30d" value={summary.paid_month}/>
            <Card label="Pending (grace)" value={summary.pending}/>
          </div>
        )}

        <div className="p-4 rounded-lg bg-white border border-[#E5E2D9]">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-medium">Mark selected as paid:</div>
            <input value={reference} onChange={e => setReference(e.target.value)}
                    placeholder="UTR / cheque / UPI reference *"
                    data-testid="payouts-reference"
                    className="h-9 px-2 border border-[#D9D5C6] rounded-md text-xs flex-1 min-w-[160px]"/>
            <input value={note} onChange={e => setNote(e.target.value)}
                    placeholder="Internal note (optional)"
                    data-testid="payouts-note"
                    className="h-9 px-2 border border-[#D9D5C6] rounded-md text-xs flex-1 min-w-[160px]"/>
            <button onClick={markPaid} disabled={busy || selected.size === 0 || !reference.trim()}
                     data-testid="payouts-mark-paid"
                     className="h-9 px-3 rounded-md bg-[#2D5A27] text-white text-xs flex items-center gap-1 disabled:opacity-50">
              <CheckSquare size={14}/> Mark {selected.size} paid
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {groups.length === 0 && (
            <div className="p-8 text-center text-sm text-[#8A8780]">No earnings yet.</div>
          )}
          {groups.map(g => (
            <div key={g.partner_id} className="bg-white border border-[#E5E2D9] rounded-lg overflow-hidden"
                 data-testid={`payouts-group-${g.partner_id}`}>
              <div className="px-4 py-2.5 bg-[#F7F6F2] flex items-center justify-between">
                <div className="font-medium">{g.partner_name}</div>
                <div className="text-xs text-[#5C5A55] flex gap-4">
                  <span>Orders: {g.orders}</span>
                  <span>Pending ₹{g.pending.toFixed(2)}</span>
                  <span className="text-[#2D5A27] font-medium">Payable ₹{g.payable.toFixed(2)}</span>
                  <span>Paid ₹{g.paid.toFixed(2)}</span>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-wider text-[#8A8780]">
                  <tr>
                    <th className="px-4 py-2 w-10"></th>
                    <th className="px-4 py-2 text-left">Date</th>
                    <th className="px-4 py-2 text-left">SR</th>
                    <th className="px-4 py-2 text-right">Distance</th>
                    <th className="px-4 py-2 text-right">Earning</th>
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-left">Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {g.earnings.map(e => (
                    <tr key={e.id} className="border-t border-[#F0EEE4]">
                      <td className="px-4 py-2">
                        {e.status === "payable" && (
                          <input type="checkbox" checked={selected.has(e.id)}
                                  onChange={() => toggle(e.id)}
                                  data-testid={`payouts-check-${e.id}`}/>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs">{e.created_at?.slice(0, 16)}</td>
                      <td className="px-4 py-2 text-xs">{e.sr_id?.slice(0, 8)}</td>
                      <td className="px-4 py-2 text-xs text-right">{e.distance_km} km</td>
                      <td className="px-4 py-2 text-right font-medium">₹{e.estimated_earning}</td>
                      <td className="px-4 py-2 text-xs">
                        <span className={`px-2 py-0.5 rounded ${
                          e.status === "paid" ? "bg-[#C7E0C2] text-[#2D5A27]" :
                          e.status === "payable" ? "bg-[#F9E4B7] text-[#7A5A11]" :
                          "bg-[#F0EEE4] text-[#5C5A55]"}`}>
                          {e.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-[#5C5A55]">
                        {e.paid_reference || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
